
import React, { useState, memo, useEffect, useRef } from "react";
import { Plus, ChevronDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useBrandManager, Brand } from "@/hooks/useBrandManager";
import { BrandItem } from "./BrandItem";
import { CreateBrandDialog } from "./CreateBrandDialog";
import { BrandSelectorProps } from "./types";

const BrandSelector = memo(({ onBrandSelect, currentBrandId }: BrandSelectorProps) => {
  const {
    brands,
    currentBrand,
    createBrand,
    selectBrand
  } = useBrandManager();

  const [newBrandOpen, setNewBrandOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const filterInputRef = useRef<HTMLInputElement>(null);

  // Special purpose brand IDs
  const specialBrandIds = ['4fc6b2bd-dd18-4b38-9954-72a011af3313', 'c6778786-86e6-4fd8-b9ca-859135c5506f'];

  // Filter and sort brands
  const filterBrands = (brandList: Brand[]) => {
    return brandList
      .filter(brand => brand.name.toLowerCase().includes(filterText.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // Get special brands that the user actually has access to
  const specialBrands = filterBrands(brands.filter(brand => 
    specialBrandIds.includes(brand.id)
  ));

  const filteredSpecialBrands = specialBrands.filter(brand => 
    brand.name.toLowerCase().includes(filterText.toLowerCase())
  );

  // Group brands by ownership vs participation (excluding special brands)
  const ownedBrands = filterBrands(brands.filter(brand => 
    brand.role === 'owner' && !specialBrandIds.includes(brand.id)
  ));
  const participatedBrands = filterBrands(brands.filter(brand => 
    brand.role !== 'owner' && !specialBrandIds.includes(brand.id)
  ));

  const handleBrandSelect = (brand: Brand) => {
    
    // Always call selectBrand from useBrandManager first
    selectBrand(brand.id);
    
    // Then call the optional callback
    if (onBrandSelect) {
      onBrandSelect(brand.id);
    }
  };

  const handleCreateBrand = async (name: string) => {
    const result = await createBrand(name);
    return result;
  };

  // Listen for onboarding event to open the Create Brand dialog directly
  useEffect(() => {
    const handler = (_e: Event) => {
      setDropdownOpen(false);
      setNewBrandOpen(true);
    };
    window.addEventListener('onboarding:open-create-brand', handler as EventListener);
    return () => window.removeEventListener('onboarding:open-create-brand', handler as EventListener);
  }, []);

  // Reset filter when dropdown closes and focus when opened
  useEffect(() => {
    if (!dropdownOpen) {
      setFilterText("");
    } else {
      // Focus the input when dropdown opens
      setTimeout(() => {
        filterInputRef.current?.focus();
      }, 100);
    }
  }, [dropdownOpen]);

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="w-48 justify-between bg-card hover:bg-muted/50 border-border hover:border-primary/50 transition-all duration-200 shadow-sm hover:shadow-md"
            data-onboarding-target="brand-selector"
          >
            <div className="flex items-center gap-2">
              {currentBrand?.favicon_url ? (
                <div className="w-5 h-5 flex items-center justify-center rounded ring-2 ring-primary">
                  <img 
                    src={currentBrand.favicon_url} 
                    alt={`${currentBrand.name} favicon`}
                    className="w-full h-full object-contain rounded"
                  />
                </div>
              ) : (
                <div className="w-2 h-2 bg-primary rounded-full"></div>
              )}
              <span className="truncate font-medium text-foreground">
                {currentBrand?.name || "Select Brand"}
              </span>
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-64 bg-popover border border-border shadow-lg rounded-lg p-0 max-h-[32rem] flex flex-col z-50"
        >
          {/* Filter Field */}
          <div className="px-2 py-2 sticky top-0 bg-popover z-50 border-b border-border rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                ref={filterInputRef}
                type="text"
                placeholder="Filter brands..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="pl-8 h-8 text-sm"
                autoComplete="off"
                data-brand-filter
              />
            </div>
          </div>

          {/* Scrollable content */}
          <div className="overflow-y-auto p-1 flex-1">

          {/* Special Purposes Brands */}
          {filteredSpecialBrands.length > 0 && (
            <>
              <div className="px-2 py-1 mt-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Special Purposes Brands
                </span>
              </div>
              
              {filteredSpecialBrands.map((brand) => (
                <DropdownMenuItem 
                  key={brand.id}
                  className="p-0 focus:bg-muted/50 rounded-md"
                >
                  <BrandItem 
                    brand={brand}
                    isSelected={currentBrand?.id === brand.id}
                    onSelect={handleBrandSelect}
                  />
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="my-1" />
            </>
          )}

          {ownedBrands.length > 0 && (
            <>
              <div className="px-2 py-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Brands You Own
                </span>
              </div>
              
              {ownedBrands.map((brand) => (
                <DropdownMenuItem 
                  key={brand.id}
                  className="p-0 focus:bg-muted/50 rounded-md"
                >
                  <BrandItem 
                    brand={brand}
                    isSelected={currentBrand?.id === brand.id}
                    onSelect={handleBrandSelect}
                  />
                </DropdownMenuItem>
              ))}
            </>
          )}

          {participatedBrands.length > 0 && (
            <>
              {ownedBrands.length > 0 && <DropdownMenuSeparator className="my-1" />}
              
              <div className="px-2 py-1">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Brands You Participate
                </span>
              </div>
              
              {participatedBrands.map((brand) => (
                <DropdownMenuItem 
                  key={brand.id}
                  className="p-0 focus:bg-muted/50 rounded-md"
                  onClick={() => handleBrandSelect(brand)}
                >
                  <div className="flex items-center justify-between cursor-pointer p-2 w-full group hover:bg-muted/50 rounded-md transition-colors duration-200">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className={`w-2 h-2 rounded-full mr-2 ${currentBrand?.id === brand.id ? 'bg-primary' : 'bg-muted'}`}></div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className={`truncate text-sm font-medium ${currentBrand?.id === brand.id ? 'text-primary' : 'text-foreground'}`}>
                          {brand.name}
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {brand.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </>
          )}
          
          {(ownedBrands.length > 0 || participatedBrands.length > 0) && <DropdownMenuSeparator className="my-1" />}
          
          <DropdownMenuItem 
            onClick={() => {
              setDropdownOpen(false);
              setTimeout(() => setNewBrandOpen(true), 100);
            }}
            className="flex items-center cursor-pointer p-2 focus:bg-muted/50 rounded-md transition-colors duration-200 group"
          >
            <Plus className="h-4 w-4 text-muted-foreground mr-2" />
            <span className="text-sm font-medium text-foreground">New Brand</span>
          </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateBrandDialog 
        open={newBrandOpen} 
        onOpenChange={setNewBrandOpen} 
        onCreateBrand={handleCreateBrand} 
      />
    </>
  );
});

BrandSelector.displayName = "BrandSelector";

export default BrandSelector;
